import React from 'react';
import AkButton from '@atlaskit/button';
import {
  GLOBAL_NAMESPACE_PREFIX,
  ButtonClassnameBase,
  ButtonClassnameSubtle as SUBTLE,
  ButtonDangerActiveBackgroundColor,
  ButtonDangerBackgroundColor,
  ButtonDangerColor,
  ButtonDangerHoverBackgroundColor,
  ButtonDefaultActiveBackgroundColor,
  ButtonDefaultActiveColor,
  ButtonDefaultBackgroundColor,
  ButtonDefaultColor,
  ButtonDefaultDisabledBackgroundColor,
  ButtonDefaultDisabledColor,
  ButtonDefaultFontSize,
  ButtonDefaultHoverBackgroundColor,
  ButtonDefaultLineHeight,
  ButtonDefaultPaddingX,
  ButtonDefaultPaddingY,
  ButtonDefaultSelectedBackgroundColor,
  ButtonDefaultSelectedColor,
  ButtonPrimaryActiveBackgroundColor,
  ButtonPrimaryBackgroundColor,
  ButtonPrimaryColor,
  ButtonPrimaryHoverBackgroundColor,
  ButtonSubtleBackgroundColor,
  ButtonSubtleColor,
  ButtonSubtleHoverBackgroundColor,
  ButtonSubtleActiveBackgroundColor,
  ComponentClassnameActive as ACTIVE,
  ComponentClassnameDefault as DEFAULT,
  ComponentClassnamePrimary as PRIMARY,
  ComponentClassnameDanger as DANGER,
  ComponentClassnameHover as HOVER,
  ComponentClassnameDisabled as DISABLED,
  ComponentClassnameSelected as SELECTED,
  ComponentClassnameFocus as FOCUS,
  ComponentClassnameFocusSelected as FOCUSSELECTED,
  ComponentClassnameStatic,
} from '../../../tokens';

export { ButtonGroup } from '@atlaskit/button';

export const ButtonClasses = {
  // For usage with static buttons
  BUTTON: `${GLOBAL_NAMESPACE_PREFIX}${ButtonClassnameBase}`,
  PRIMARY: `${GLOBAL_NAMESPACE_PREFIX}${ButtonClassnameBase}--${PRIMARY}`,
  DANGER: `${GLOBAL_NAMESPACE_PREFIX}${ButtonClassnameBase}--${DANGER}`,
  SUBTLE: `${GLOBAL_NAMESPACE_PREFIX}${ButtonClassnameBase}--${SUBTLE}`,
  SELECTED: `${GLOBAL_NAMESPACE_PREFIX}${ButtonClassnameBase}--${SELECTED}`,
  STATIC: `${GLOBAL_NAMESPACE_PREFIX}${ButtonClassnameBase}--${ComponentClassnameStatic}`,
};
type ButtonAppearance =
  | typeof DEFAULT
  | typeof PRIMARY
  | typeof DANGER
  | typeof SUBTLE;

type ButtonState =
  | typeof DEFAULT
  | typeof HOVER
  | typeof ACTIVE
  | typeof FOCUS
  | typeof SELECTED
  | typeof DISABLED
  | typeof FOCUSSELECTED;

type ButtonStyleDict = {
  readonly [Appearance in ButtonAppearance]: {
    readonly [State in ButtonState]?: {
      readonly [property: string]: string;
    };
  } & { readonly default: React.CSSProperties }; // must have default state styles
};

interface ButtonThemeProps {
  state: ButtonState;
  appearance: ButtonAppearance;
}
// button state styles MUST have a 'default' key
// of set properties for a fallback
const buttonStyleDict: ButtonStyleDict = {
  [DEFAULT]: {
    [DEFAULT]: {
      backgroundColor: ButtonDefaultBackgroundColor,
      color: ButtonDefaultColor,
      fontSize: ButtonDefaultFontSize,
      lineHeight: ButtonDefaultLineHeight,
      padding: `${ButtonDefaultPaddingY} ${ButtonDefaultPaddingX}`,
    },
    [HOVER]: {
      backgroundColor: ButtonDefaultHoverBackgroundColor,
    },
    // focus and active states share styles
    [ACTIVE]: {
      color: ButtonDefaultActiveColor,
      backgroundColor: ButtonDefaultActiveBackgroundColor,
    },
    [FOCUS]: {
      color: ButtonDefaultActiveColor,
      backgroundColor: ButtonDefaultActiveBackgroundColor,
    },
    // when something is selected and focused
    [FOCUSSELECTED]: {
      color: ButtonDefaultActiveColor,
      backgroundColor: ButtonDefaultActiveBackgroundColor,
    },
    [DISABLED]: {
      backgroundColor: ButtonDefaultDisabledBackgroundColor,
      color: ButtonDefaultDisabledColor,
    },
    [SELECTED]: {
      backgroundColor: ButtonDefaultSelectedBackgroundColor,
      color: ButtonDefaultSelectedColor,
    },
  },
  [PRIMARY]: {
    [DEFAULT]: {
      backgroundColor: ButtonPrimaryBackgroundColor,
      color: ButtonPrimaryColor,
    },
    [HOVER]: {
      backgroundColor: ButtonPrimaryHoverBackgroundColor,
    },
    [ACTIVE]: {
      backgroundColor: ButtonPrimaryActiveBackgroundColor,
    },
    [FOCUS]: {
      backgroundColor: ButtonPrimaryActiveBackgroundColor,
    },
  },
  [DANGER]: {
    [DEFAULT]: {
      backgroundColor: ButtonDangerBackgroundColor,
      color: ButtonDangerColor,
    },
    [HOVER]: {
      backgroundColor: ButtonDangerHoverBackgroundColor,
    },
    [ACTIVE]: {
      backgroundColor: ButtonDangerActiveBackgroundColor,
    },
    [FOCUS]: {
      backgroundColor: ButtonDangerActiveBackgroundColor,
    },
  },
  [SUBTLE]: {
    [DEFAULT]: {
      backgroundColor: ButtonSubtleBackgroundColor,
      color: ButtonSubtleColor,
    },
    [HOVER]: {
      backgroundColor: ButtonSubtleHoverBackgroundColor,
    },
    [ACTIVE]: {
      backgroundColor: ButtonSubtleActiveBackgroundColor,
    },
    [FOCUS]: {
      backgroundColor: ButtonSubtleActiveBackgroundColor,
    },
  },
};

// this logic is to prevent errors occuring when we try to look up
// an appearance or an appearance-state pairing that doesn't have a
// corresponding style property
const getButtonStyleProp = (
  appearance: ButtonAppearance,
  state: ButtonState,
  prop: keyof React.CSSProperties,
) => {
  const defaultAppearanceStyles = buttonStyleDict[DEFAULT];
  const defaultStateStyles = buttonStyleDict[DEFAULT][DEFAULT];

  // if the button styles have the appearance, take on that object for styles.
  // Otherwise, fallback to the default.
  const appearanceStyles = buttonStyleDict[appearance]
    ? buttonStyleDict[appearance]
    : defaultAppearanceStyles;

  // if the appearance AND corresponding state exist, use those styles.
  // If the appearance does NOT have the corresponding state,
  // use the styles that the default appearance has for that state
  const appearanceStateStyles =
    appearanceStyles[state] !== undefined
      ? appearanceStyles[state]
      : defaultAppearanceStyles[state];
  // if we have all the proper entries for the styles, return the style property
  // if we DON'T have a style to return, return the default styles of the
  // appearance
  // if there are no default styles for the appearance, fallback to the default
  // appearance and default state.
  return appearanceStateStyles![prop]
    ? appearanceStateStyles![prop]
    : appearanceStyles[DEFAULT][prop]
    ? appearanceStyles[DEFAULT][prop]
    : defaultAppearanceStyles![state]![prop]
    ? defaultAppearanceStyles![state]![prop]
    : defaultStateStyles[prop];
};

const customTheme = (
  currentTheme: (
    props: ButtonThemeProps,
  ) => {
    buttonStyles: React.CSSProperties;
    spinnerStyles: React.CSSProperties;
  },
  themeProps: ButtonThemeProps,
) => {
  const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
  const { state, appearance } = themeProps;

  return {
    buttonStyles: {
      ...buttonStyles,
      boxShadow: 'none',
      color: getButtonStyleProp(appearance, state, 'color'),
      backgroundColor: getButtonStyleProp(appearance, state, 'backgroundColor'),
      fontSize: getButtonStyleProp(appearance, state, 'fontSize'),
      lineHeight: getButtonStyleProp(appearance, state, 'lineHeight'),
      padding: getButtonStyleProp(appearance, state, 'padding'),
    },
    spinnerStyles,
  };
};

export interface ButtonProps {
  appearance?: ButtonAppearance;
  /**
   * should the button get focused on mount
   * @default false
   */
  autoFocus?: boolean;
  /**
   * A string of classnames to be applied
   *
   * @default ''
   **/
  className?: string;
  /**
   * Is the button disabled
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Is the button in a state of loading (async)
   * @default false
   */
  isLoading?: boolean;
  /**
   * Is the button selected
   * @default false
   */
  isSelected?: boolean;
  /* Handle blur events on the control */
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /* Handle change events on the select */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /* Handle focus events on the control */
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /* Handle mouse down events on the control */
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /* Handle mouse enter events on the control */
  onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /* Handle mouse leave events on the control */
  onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /* Handle mouse up events on the control */
  onMouseUp?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Option to fit button width to its parent width
   * @default false
   */
  shouldFitContainer?: boolean;
  style?: React.CSSProperties;
  iconAfter?: React.ReactChild;
  type?: string;
}

export const Button: React.FunctionComponent<ButtonProps> = (props) => {
  const { children } = props;

  return (
    <AkButton theme={customTheme} {...props}>
      {children}
    </AkButton>
  );
};
